import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Money,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import { slotStatusLabel } from '@mlm/shared';
import { adminListSlots, listMemberships } from '../lib/api';
import { CreateSlotDialog } from '../components/slots/create-slot-dialog';
import { AdjustDialog } from '../components/adjust-dialog';
import { useHasPerm } from '../lib/auth-store';

export function SlotsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('');
  const [membershipCode, setMembershipCode] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [createOpen, setCreateOpen] = useState(false);
  const canAdjust = useHasPerm('member.balance.adjust');
  const canSeeIncome = useHasPerm('member.income.view');

  const query = useQuery({
    queryKey: ['admin-slots', search, status, membershipCode, page, pageSize],
    queryFn: () =>
      adminListSlots({
        page,
        pageSize,
        ownerSearch: search || undefined,
        status: (status || undefined) as
          | 'pending_payment'
          | 'active'
          | 'suspended'
          | 'cancelled'
          | undefined,
        membershipCode: membershipCode || undefined,
      }),
  });
  const memberships = useQuery({ queryKey: ['memberships'], queryFn: listMemberships });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Slots</h1>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          + New Slot
        </Button>
      </div>
      <CreateSlotDialog open={createOpen} onOpenChange={setCreateOpen} />

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="ownerSearch">
              Owner / slot code
            </label>
            <Input
              id="ownerSearch"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="email, name, or slot code"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Status</label>
            <Select
              value={status || '__all__'}
              onValueChange={(v) => {
                setStatus(v === '__all__' ? '' : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                <SelectItem value="active">{slotStatusLabel('active')}</SelectItem>
                <SelectItem value="suspended">{slotStatusLabel('suspended')}</SelectItem>
                <SelectItem value="cancelled">{slotStatusLabel('cancelled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Membership</label>
            <Select
              value={membershipCode || '__all__'}
              onValueChange={(v) => {
                setMembershipCode(v === '__all__' ? '' : v);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {(memberships.data ?? []).map((m) => (
                  <SelectItem key={m.id} value={m.code}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Per page</label>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 100].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {query.data && (
              <span>
                Slots{' '}
                <span className="text-sm font-normal text-muted-foreground">
                  ({query.data.total} total · page {page})
                </span>
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {query.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : (query.data?.rows.length ?? 0) === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No slots match.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2">Slot code</th>
                  <th className="px-4 py-2">Owner</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Tier</th>
                  {query.data?.stairstepActive && <th className="px-4 py-2">Rank</th>}
                  <th className="px-4 py-2">Sponsor</th>
                  <th className="px-4 py-2" title="The slot the owner's signup link pointed at">
                    Link sponsor
                  </th>
                  {canSeeIncome && <th className="px-4 py-2 text-right">Current wallet</th>}
                  {canSeeIncome && <th className="px-4 py-2 text-right">Total earned</th>}
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Activated</th>
                  {canAdjust && <th className="px-4 py-2 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {query.data!.rows.map((s) => (
                  <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2 font-mono text-xs">
                      <Link to={`/slots/${s.id}`} className="hover:underline text-foreground">
                        {s.slotCode}
                      </Link>
                      {s.isCompany && (
                        <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                          Company
                        </span>
                      )}
                      {s.rankShowcaseHidden && (
                        <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                          Hidden from showcase
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Link to={`/members/${s.userId}`} className="hover:underline">
                        {s.userFullName ?? s.userEmail}
                      </Link>
                    </td>
                    {/* Its own column rather than a line under the name: the
                        Owner cell falls back to the email only for members with
                        no name, so a named member's email was unreachable from
                        this list even though `ownerSearch` matches on it. */}
                    <td className="px-4 py-2 text-xs text-muted-foreground">{s.userEmail}</td>
                    <td className="px-4 py-2">{s.membershipName}</td>
                    {query.data?.stairstepActive && (
                      <td className="px-4 py-2">
                        {s.rankCode ? (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 capitalize">
                            {s.rankCode}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-2 text-sm">
                      {s.sponsorSlotCode ? (
                        <span>
                          {s.sponsorFullName ?? s.sponsorEmail ?? 'Unknown'}{' '}
                          <span className="font-mono text-xs text-muted-foreground">
                            ({s.sponsorSlotCode})
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    {/* Replicated-link sponsor — the slot the OWNER's signup
                        `?ref=` resolved to. Frequently equals the Sponsor cell,
                        but diverges on an override, a typed sponsor at redeem,
                        or a 2nd+ self-stacked slot. */}
                    <td className="px-4 py-2 text-sm">
                      {s.linkSponsorSlotCode ? (
                        <span>
                          {s.linkSponsorFullName ?? s.linkSponsorEmail ?? 'Unknown'}{' '}
                          <span className="font-mono text-xs text-muted-foreground">
                            ({s.linkSponsorSlotCode})
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    {canSeeIncome && (
                      <td className="px-4 py-2 text-right">
                        <Link
                          to={`/slots/${s.id}?tab=logs`}
                          className="hover:underline"
                          title="View wallet history"
                        >
                          <Money amount={s.walletAvailable ?? '0'} direction="neutral" />
                        </Link>
                      </td>
                    )}
                    {canSeeIncome && (
                      <td className="px-4 py-2 text-right">
                        <Link
                          to={`/slots/${s.id}?tab=wallet`}
                          className="hover:underline"
                          title="View wallet breakdown"
                        >
                          <Money amount={s.lifetimeEarned ?? '0'} direction="neutral" />
                        </Link>
                      </td>
                    )}
                    <td className="px-4 py-2">
                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted">
                        {slotStatusLabel(s.status)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {s.activatedAt ? new Date(s.activatedAt).toLocaleString() : ''}
                    </td>
                    {canAdjust && (
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-1.5">
                          <AdjustDialog
                            kind="wallet"
                            compact
                            userId={s.userId}
                            slotId={s.id}
                            slotCode={s.slotCode}
                          />
                          <AdjustDialog
                            kind="points"
                            compact
                            userId={s.userId}
                            slotId={s.id}
                            slotCode={s.slotCode}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {query.data && query.data.total > query.data.pageSize && (
            <div className="flex justify-end gap-2 p-3">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="text-sm px-2 py-1 rounded border disabled:opacity-50"
              >
                Prev
              </button>
              <button
                disabled={page * query.data.pageSize >= query.data.total}
                onClick={() => setPage((p) => p + 1)}
                className="text-sm px-2 py-1 rounded border disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
